import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Lock, Unlock, Moon, CalendarOff, Palmtree, Clock, XCircle, UserX } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime, inr } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const STATUS_UI = {
  submitted: { icon: CheckCircle2, cls: "text-green-700", label: "Performance Submitted" },
  no_walkin: { icon: UserX, cls: "text-slate-700", label: "No Walk-in" },
  pending: { icon: AlertCircle, cls: "text-red-700", label: "Pending" },
  off: { icon: Moon, cls: "text-slate-400", label: "Off" },
  week_off: { icon: CalendarOff, cls: "text-slate-500", label: "Week Off" },
  leave: { icon: Palmtree, cls: "text-[#8a6d2f]", label: "Leave · Approved" },
  leave_pending: { icon: Clock, cls: "text-amber-700", label: "Leave · Awaiting approval" },
  leave_rejected: { icon: XCircle, cls: "text-red-700", label: "Leave · Rejected" },
};

export default function SubmissionTracker({ storeId }) {
  const { settings } = useAuth();
  const [date, setDate] = useState(settings.effective_date);
  const [d, setD] = useState(null);
  const [reopen, setReopen] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(() => api.get("/submissions", { params: { store_id: storeId, date } }).then((r) => setD(r.data)), [storeId, date]);
  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    try {
      await api.post("/closings", { store_id: storeId, date, action, reason });
      toast.success(action === "close" ? "Day closed" : "Day reopened");
      setReopen(false);
      setReason("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const decide = async (id, approve) => {
    try {
      await api.post(`/day-status/${id}/decide`, null, { params: { approve } });
      toast.success(approve ? "Leave approved" : "Leave rejected");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!d) return null;
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 bs-card">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-slate-100">
          <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-48 h-11" data-testid="submission-date-input" />
          {d.pending > 0 ? (
            <div className="rounded-full bg-red-50 text-red-700 border border-red-200 px-4 py-1.5 font-semibold text-sm" data-testid="pending-count">{d.pending} pending</div>
          ) : (
            <div className="rounded-full bg-green-50 text-green-700 border border-green-200 px-4 py-1.5 font-semibold text-sm" data-testid="pending-count">All submitted</div>
          )}
        </div>
        <table className="w-full text-left" data-testid="submission-table">
          <thead className="text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-5 py-3">Staff</th><th className="px-5 py-3">Today's Entry</th><th className="px-5 py-3 text-right">Sales</th><th className="px-5 py-3">Status</th></tr></thead>
          <tbody>
            {d.rows.map((r) => {
              const u = STATUS_UI[r.status];
              return (
                <tr key={r.id} className={`border-t border-slate-50 ${r.status === "pending" ? "bg-red-50/40" : ""}`} data-testid={`submission-row-${r.employee_id}`}>
                  <td className="px-5 py-4 font-semibold text-[#011E38]">{r.name}<div className="text-xs text-slate-500 font-normal">{r.employee_id}</div></td>
                  <td className={`px-5 py-3 font-semibold ${u.cls}`}>{u.label}{r.updated_at && <div className="text-xs text-slate-400 font-normal">{fmtDateTime(r.updated_at)}</div>}
                    {r.leave_reason && <div className="text-xs text-slate-500 font-normal italic">“{r.leave_reason}”</div>}
                    {r.status.startsWith("leave") && (
                      <div className="flex gap-2 mt-2">
                        {r.status !== "leave" && <Button size="sm" className="h-8 bg-green-700 hover:bg-green-800" onClick={() => decide(r.day_status_id, true)} data-testid={`leave-approve-${r.employee_id}`}>Approve</Button>}
                        {r.status !== "leave_rejected" && <Button size="sm" variant="outline" className="h-8 text-red-700 border-red-200" onClick={() => decide(r.day_status_id, false)} data-testid={`leave-reject-${r.employee_id}`}>Reject</Button>}
                      </div>
                    )}
                  </td>
                  <td className="px-5 text-right num">{r.sales != null ? inr(r.sales) : "—"}</td>
                  <td className="px-5"><u.icon className={`w-5 h-5 ${u.cls}`} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="lg:col-span-4 bs-card p-6 space-y-4" data-testid="closing-panel">
        <div className="bs-eyebrow">Daily closing</div>
        <div className="flex items-center gap-2 font-display text-2xl font-bold text-[#011E38]" data-testid="closing-status">
          {d.closed ? <><Lock className="w-6 h-6" /> Closed</> : <><Unlock className="w-6 h-6" /> Open</>}
        </div>
        <p className="text-sm text-slate-500">{d.closed ? "Staff cannot edit entries for this day." : "Closing locks this day for staff edits."}</p>
        {d.closed ? (
          <Button variant="outline" className="w-full h-12" onClick={() => setReopen(true)} data-testid="reopen-day-button">Reopen day</Button>
        ) : (
          <Button className="w-full h-12 bg-[#011E38] hover:bg-[#032C52]" onClick={() => act("close")} data-testid="close-day-button">Close day</Button>
        )}
        {d.history.length > 0 && (
          <div className="pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-500" data-testid="closing-history">
            {d.history.slice().reverse().map((h, i) => (
              <div key={i}><span className="font-semibold text-slate-700">{h.action === "close" ? "Closed" : "Reopened"}</span> by {h.by} · {fmtDateTime(h.at)}{h.reason && <div className="italic">“{h.reason}”</div>}</div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={reopen} onOpenChange={setReopen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reopen {date}</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reopen-reason-input" />
          <DialogFooter>
            <Button disabled={!reason.trim()} onClick={() => act("reopen")} className="bg-[#011E38]" data-testid="reopen-confirm-button">Reopen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
